import { Direction } from "../engine/direction";
import { getPlayerBodyFrames, getPlayerWeaponOverlayFilename, type PlayerBodyFrame } from "./playerSpriteConfig";
import { playerVisualStateKey, type PlayerVisualState } from "./playerVisualState";
import { Sprite } from "./sprites/sprite";
import { SpriteAnimation } from "./sprites/spriteAnimation";
import { getImage } from "./tileImageLoader";

export const PLAYER_SPRITE_WIDTH_CELLS = 1;
export const PLAYER_SPRITE_HEIGHT_CELLS = 2;

// Bob offsets are authored in classic-source pixels, where one cell is 20px.
const CLASSIC_CELL_SOURCE_PX = 20;

interface PlayerSpriteLayer {
	filename: string;
	dx?: number;
	dy?: number;
}

export class PlayerSpriteComposer {
	private frameCache = new Map<string, Promise<HTMLCanvasElement>>();

	async getAnimation(
		state: PlayerVisualState,
		facing: Direction,
		cellWidth: number,
		cellHeight: number,
	): Promise<SpriteAnimation> {
		const frames = getPlayerBodyFrames(state.characterId, state.bodyState);
		const pixelWidth = Math.floor(cellWidth * PLAYER_SPRITE_WIDTH_CELLS);
		const pixelHeight = Math.floor(cellHeight * PLAYER_SPRITE_HEIGHT_CELLS);

		const animation = new SpriteAnimation(true, []);
		for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
			const frame = frames[frameIndex];
			const key = this.frameCacheKey(state, frameIndex, facing, pixelWidth, pixelHeight);
			const sprite = new Sprite();
			sprite.assetName = frame.filename;
			sprite.image = await this.getFrameCanvas(key, state, frame, facing, pixelWidth, pixelHeight);
			sprite.width = PLAYER_SPRITE_WIDTH_CELLS;
			sprite.height = PLAYER_SPRITE_HEIGHT_CELLS;
			sprite.flipH = facing === Direction.W;
			animation.addFrame(sprite, frame.interval);
		}
		return animation;
	}

	clearCache(): void {
		this.frameCache.clear();
	}

	private frameCacheKey(
		state: PlayerVisualState,
		frameIndex: number,
		facing: Direction,
		pixelWidth: number,
		pixelHeight: number,
	): string {
		return `${playerVisualStateKey(state)}|${frameIndex}|${facing}|${pixelWidth}x${pixelHeight}`;
	}

	private getFrameCanvas(
		key: string,
		state: PlayerVisualState,
		frame: PlayerBodyFrame,
		facing: Direction,
		pixelWidth: number,
		pixelHeight: number,
	): Promise<HTMLCanvasElement> {
		const cached = this.frameCache.get(key);
		if (cached) return cached;

		const composed = this.composeFrame(state, frame, facing, pixelWidth, pixelHeight);
		this.frameCache.set(key, composed);
		return composed;
	}

	private async composeFrame(
		state: PlayerVisualState,
		frame: PlayerBodyFrame,
		facing: Direction,
		pixelWidth: number,
		pixelHeight: number,
	): Promise<HTMLCanvasElement> {
		const layers = this.buildLayers(state, frame, pixelWidth, pixelHeight);
		const images = await Promise.all(layers.map((layer) => getImage(layer.filename, pixelWidth, pixelHeight)));

		const canvas = document.createElement("canvas");
		canvas.width = pixelWidth;
		canvas.height = pixelHeight;
		const ctx = canvas.getContext("2d")!;
		if (facing === Direction.W) {
			ctx.scale(-1, 1);
			ctx.translate(-pixelWidth, 0);
		}
		for (let i = 0; i < images.length; i++) {
			ctx.drawImage(images[i], layers[i].dx ?? 0, layers[i].dy ?? 0);
		}
		return canvas;
	}

	private buildLayers(
		state: PlayerVisualState,
		frame: PlayerBodyFrame,
		pixelWidth: number,
		pixelHeight: number,
	): PlayerSpriteLayer[] {
		const cellWidth = pixelWidth / PLAYER_SPRITE_WIDTH_CELLS;
		const cellHeight = pixelHeight / PLAYER_SPRITE_HEIGHT_CELLS;
		return [
			{ filename: frame.filename },
			{
				filename: getPlayerWeaponOverlayFilename(state.weaponVisual),
				dx: Math.round(frame.bobOffsetX * (cellWidth / CLASSIC_CELL_SOURCE_PX)),
				dy: Math.round(frame.bobOffsetY * (cellHeight / CLASSIC_CELL_SOURCE_PX)),
			},
		];
	}
}

export const playerSpriteComposer = new PlayerSpriteComposer();
